import Papa from 'papaparse';

export interface CsvParseError<Result = unknown> {
  row: number;
  column: string | number;
  field?: string;
  message: string;
  value?: Result;
  code?: string;
}

export interface CsvParseResult<T> {
  data: T[];
  errors: CsvParseError[];
  totalRows: number;
  validRows: number;
}

export interface ColumnDefinition<Result = unknown, Row = unknown[]> {
  /** Column index (0-based) or header name */
  key: string | number;
  /** Field name in the output object */
  field: string;
  /** Whether this column is required */
  required?: boolean;
  /** Default value if column is empty */
  defaultValue?: Result;
  /** Custom parser function for this column */
  parser?: (value: string, row: Row, rowIndex: number) => Result | Promise<Result>;
  /** Validation function for this column */
  validator?: (value: Result, row: Row, rowIndex: number) => string | null | Promise<string | null>;
  /** Transform function applied after parsing and validation */
  transformer?: (value: Result, row: Row, rowIndex: number) => Result;
}

export interface CsvParserOptions<T> {
  /** Column definitions */
  columns: ColumnDefinition[];
  /** Whether the CSV has a header row */
  hasHeader?: boolean;
  /** Skip empty rows */
  skipEmptyRows?: boolean;
  /** Custom row validator (runs after all column validations) */
  rowValidator?: (
    data: Partial<T>,
    row: string[],
    rowIndex: number,
  ) => string[] | Promise<string[]>;
  /** Custom row transformer (runs after all column transformations) */
  rowTransformer?: (data: Partial<T>, row: string[], rowIndex: number) => T | Promise<T>;
  /** Maximum number of errors to collect before stopping */
  maxErrors?: number;
  /** Papa Parse configuration overrides */
  papaParseConfig?: Papa.ParseConfig;
  /** Translation function for error messages */
  translate?: (key: string, options?: any) => string;
}

export class CsvResultTransformer<T = Record<string, any>> {
  private options: Required<Omit<CsvParserOptions<T>, 'translate'>> & {
    translate?: (key: string, options?: any) => string;
  };

  constructor(options: CsvParserOptions<T>) {
    this.options = {
      hasHeader: true,
      skipEmptyRows: true,
      maxErrors: 100,
      rowValidator: () => [],
      rowTransformer: (data) => data as T,
      papaParseConfig: {},
      ...options,
    };
  }

  /**
   * Process the Papa Parse result
   */
  async parse(result: Papa.ParseResult<string[]>): Promise<CsvParseResult<T>> {
    const { data, errors: papaErrors } = result;
    const errors: CsvParseError[] = [];
    const parsedData: T[] = [];

    // Add Papa Parse errors
    papaErrors.forEach((error: any) => {
      errors.push({
        row: error.row ?? 0,
        column: error.col ?? 0,
        message: error.message ?? this.t('csvImport.errors.parseError', 'Parse error'),
        code: error.code,
      });
    });

    // Determine data rows (skip header if present)
    const dataRows = this.options.hasHeader ? data.slice(1) : data;
    const headerRow = this.options.hasHeader ? data[0] : null;

    // Process each row
    for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
      const row = dataRows[rowIndex];
      const actualRowIndex = this.options.hasHeader ? rowIndex + 2 : rowIndex + 1; // +1 for 1-based indexing

      // Skip empty rows if configured
      if (this.options.skipEmptyRows && this.isEmptyRow(row)) {
        continue;
      }

      try {
        const { rowData, rowErrors } = await this.processRow(row, actualRowIndex, headerRow);

        // Add any row errors to the global error list
        errors.push(...rowErrors);

        if (rowData !== null) {
          parsedData.push(rowData);
        }
      } catch (error: unknown) {
        errors.push({
          row: actualRowIndex,
          column: 'row',
          message:
            error instanceof Error
              ? error.message
              : this.t('csvImport.errors.unknownError', 'Unknown error'),
          code: 'ROW_PROCESSING_ERROR',
        });
      }

      // Stop if we've hit the maximum error limit
      if (errors.length >= this.options.maxErrors) {
        break;
      }
    }

    return {
      data: parsedData,
      errors,
      totalRows: data.length,
      validRows: parsedData.length,
    };
  }

  /**
   * Process a single row
   */
  private async processRow(
    row: string[],
    rowIndex: number,
    headerRow: string[] | null,
  ): Promise<{ rowData: T | null; rowErrors: CsvParseError[] }> {
    const rowData: Partial<T> = {};
    const rowErrors: CsvParseError[] = [];

    // Process each column
    for (const columnDef of this.options.columns) {
      try {
        const columnIndex = this.resolveColumnIndex(columnDef.key, headerRow);
        const rawValue = row[columnIndex];

        // Handle required fields
        if (columnDef.required && this.isEmpty(rawValue)) {
          rowErrors.push({
            row: rowIndex,
            column: columnIndex,
            field: columnDef.field,
            message: this.t('csvImport.errors.fieldRequired', `${columnDef.field} is required`, {
              field: columnDef.field,
            }),
            value: rawValue,
            code: 'REQUIRED_FIELD_MISSING',
          });
          continue;
        }

        // Use default value if empty and not required
        let processedValue: unknown;

        if (this.isEmpty(rawValue)) {
          processedValue = columnDef.defaultValue;
        } else {
          // Apply custom parser
          if (columnDef.parser) {
            processedValue = await columnDef.parser(rawValue, row, rowIndex);
          } else {
            processedValue = rawValue;
          }
        }

        // Apply validation
        if (columnDef.validator && processedValue !== undefined) {
          const validationError = await columnDef.validator(processedValue, row, rowIndex);
          if (validationError) {
            rowErrors.push({
              row: rowIndex,
              column: columnIndex,
              field: columnDef.field,
              message: validationError,
              value: processedValue,
              code: 'VALIDATION_ERROR',
            });
            continue;
          }
        }

        // Apply transformer
        if (columnDef.transformer) {
          processedValue = columnDef.transformer(processedValue, row, rowIndex);
        }

        // Set the field value
        (rowData as any)[columnDef.field] = processedValue;
      } catch (error: any) {
        let rawValue: string | undefined;
        try {
          rawValue = row[this.resolveColumnIndex(columnDef.key, headerRow)];
        } catch {
          rawValue = undefined;
        }
        rowErrors.push({
          row: rowIndex,
          column: columnDef.key,
          field: columnDef.field,
          message:
            error.message ??
            this.t('csvImport.errors.columnProcessingFailed', 'Column processing failed'),
          value: rawValue,
          code: 'COLUMN_PROCESSING_ERROR',
        });
      }
    }

    // If there are column errors, don't process the row further
    if (rowErrors.length > 0) {
      return { rowData: null, rowErrors };
    }

    // Apply row-level validation
    const rowValidationErrors = await this.options.rowValidator(rowData, row, rowIndex);
    if (rowValidationErrors.length > 0) {
      rowValidationErrors.forEach((message) => {
        rowErrors.push({
          row: rowIndex,
          column: 'row',
          message,
          code: 'ROW_VALIDATION_ERROR',
        });
      });
      return { rowData: null, rowErrors };
    }

    // Apply row transformer
    const transformedRow = await this.options.rowTransformer(rowData, row, rowIndex);
    return { rowData: transformedRow, rowErrors };
  }

  /**
   * Resolve column index from key (string or number)
   */
  private resolveColumnIndex(key: string | number, headerRow: any[] | null): number {
    if (typeof key === 'number') {
      return key;
    }

    // If it's a string, try to find it in the header
    if (headerRow) {
      const index = headerRow.findIndex(
        (header) => header?.toString().toLowerCase().trim() === key.toLowerCase().trim(),
      );
      if (index !== -1) {
        return index;
      }
    }

    // If not found, try to parse as number
    const numericKey = parseInt(key, 10);
    if (!isNaN(numericKey)) {
      return numericKey;
    }

    throw new Error(
      this.t('csvImport.errors.columnNotFound', `Column '${key}' not found`, { column: key }),
    );
  }

  /**
   * Translation helper method
   */
  private t(key: string, fallback: string, options?: any): string {
    if (this.options.translate) {
      return this.options.translate(key, options);
    }
    return fallback;
  }

  /**
   * Check if a value is empty
   */
  private isEmpty(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '')
    );
  }

  /**
   * Check if a row is empty
   */
  private isEmptyRow(row: string[]): boolean {
    return row.every((cell) => this.isEmpty(cell));
  }
}

// Utility functions for common parsing scenarios
export const CsvUtilParsers = {
  /**
   * Parse string value
   */
  string:
    (trim = true) =>
    (value: string) => {
      return trim ? value.trim() : value;
    },

  /**
   * Parse number value
   */
  number:
    (allowNull = false, t?: (key: string, fallback: string, options?: any) => string) =>
    (value: string) => {
      if (allowNull && value === '') return null;
      const num = parseFloat(value);
      if (isNaN(num)) {
        const message = t
          ? t('csvImport.errors.invalidNumber', `Invalid number: ${value}`, { value })
          : `Invalid number: ${value}`;
        throw new Error(message);
      }
      return num;
    },

  /**
   * Parse integer value
   */
  integer:
    (allowNull = false, t?: (key: string, fallback: string, options?: any) => string) =>
    (value: string) => {
      if (allowNull && value === '') return null;
      const num = parseInt(value, 10);
      if (isNaN(num)) {
        const message = t
          ? t('csvImport.errors.invalidInteger', `Invalid integer: ${value}`, { value })
          : `Invalid integer: ${value}`;
        throw new Error(message);
      }
      return num;
    },

  /**
   * Parse boolean value
   */
  boolean:
    (
      trueValues = ['true', 'yes', '1', 'on'],
      falseValues = ['false', 'no', '0', 'off'],
      t?: (key: string, fallback: string, options?: any) => string,
    ) =>
    (value: string) => {
      const normalized = value.toLowerCase().trim();
      if (trueValues.includes(normalized)) return true;
      if (falseValues.includes(normalized)) return false;
      const message = t
        ? t('csvImport.errors.invalidBoolean', `Invalid boolean value: ${value}`, { value })
        : `Invalid boolean value: ${value}`;
      throw new Error(message);
    },

  /**
   * Parse date value
   */
  date: (t?: (key: string, fallback: string, options?: any) => string) => (value: string) => {
    if (!value || value.trim() === '') return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      const message = t
        ? t('csvImport.errors.invalidDate', `Invalid date: ${value}`, { value })
        : `Invalid date: ${value}`;
      throw new Error(message);
    }
    return date;
  },

  /**
   * Parse comma-separated values
   */
  array:
    (separator = ',', itemParser?: (item: string) => any) =>
    (value: string) => {
      if (!value || value.trim() === '') return [];
      const items = value
        .split(separator)
        .map((item) => item.trim())
        .filter((item) => item !== '');
      return itemParser ? items.map(itemParser) : items;
    },

  /**
   * Parse enum value
   */
  enum:
    <T extends string>(
      allowedValues: T[],
      caseSensitive = false,
      t?: (key: string, fallback: string, options?: any) => string,
    ) =>
    (value: string): T => {
      const normalized = caseSensitive ? value : value.toLowerCase();
      const allowed = caseSensitive ? allowedValues : allowedValues.map((v) => v.toLowerCase());
      const index = allowed.indexOf(normalized);
      if (index === -1) {
        const message = t
          ? t(
              'csvImport.errors.invalidEnum',
              `Invalid enum value: ${value}. Allowed values: ${allowedValues.join(', ')}`,
              {
                value,
                allowed: allowedValues.join(', '),
              },
            )
          : `Invalid enum value: ${value}. Allowed values: ${allowedValues.join(', ')}`;
        throw new Error(message);
      }
      return allowedValues[index];
    },
};

// Utility functions for common validation scenarios
export const CsvUtilValidators = {
  /**
   * Validate required field
   */
  required:
    (message?: string, t?: (key: string, fallback: string, options?: any) => string) =>
    (value: any) => {
      const isEmpty =
        value === null ||
        value === undefined ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '');

      if (isEmpty) {
        if (message) return message;
        return t
          ? t('csvImport.errors.fieldRequiredGeneric', 'This field is required')
          : 'This field is required';
      }
      return null;
    },

  /**
   * Validate minimum length
   */
  minLength:
    (min: number, message?: string, t?: (key: string, fallback: string, options?: any) => string) =>
    (value: string) => {
      if (value && value.length < min) {
        if (message) return message;
        return t
          ? t('csvImport.errors.minLength', `Minimum length is ${min} characters`, { min })
          : `Minimum length is ${min} characters`;
      }
      return null;
    },

  /**
   * Validate maximum length
   */
  maxLength:
    (max: number, message?: string, t?: (key: string, fallback: string, options?: any) => string) =>
    (value: string) => {
      if (value && value.length > max) {
        if (message) return message;
        return t
          ? t('csvImport.errors.maxLength', `Maximum length is ${max} characters`, { max })
          : `Maximum length is ${max} characters`;
      }
      return null;
    },

  /**
   * Validate number range
   */
  numberRange:
    (
      min?: number,
      max?: number,
      message?: string,
      t?: (key: string, fallback: string, options?: any) => string,
    ) =>
    (value: number) => {
      if (typeof value !== 'number') return null;
      if (min !== undefined && value < min) {
        if (message) return message;
        return t
          ? t('csvImport.errors.minValue', `Value must be at least ${min}`, { min })
          : `Value must be at least ${min}`;
      }
      if (max !== undefined && value > max) {
        if (message) return message;
        return t
          ? t('csvImport.errors.maxValue', `Value must be at most ${max}`, { max })
          : `Value must be at most ${max}`;
      }
      return null;
    },

  /**
   * Validate with regex
   */
  regex:
    (
      pattern: RegExp,
      message?: string,
      t?: (key: string, fallback: string, options?: any) => string,
    ) =>
    (value: string) => {
      if (value && !pattern.test(value)) {
        if (message) return message;
        return t
          ? t('csvImport.errors.patternMismatch', 'Value does not match required pattern')
          : 'Value does not match required pattern';
      }
      return null;
    },

  /**
   * Validate email format
   */
  email:
    (message?: string, t?: (key: string, fallback: string, options?: any) => string) =>
    (value: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        if (message) return message;
        return t
          ? t('csvImport.errors.invalidEmail', 'Invalid email format')
          : 'Invalid email format';
      }
      return null;
    },
};
